import React, { useState, useEffect, useRef, useContext } from "react";
import { Container } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "components/molecules/Breadcrumb";
import { CKEditor } from "ckeditor4-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { API_URL_slaplan } from "constants/api";
import { slaplanReducers } from "redux/slaplanSlice";
import { postData, putData } from "actions";
import { AuthContext } from "context/AuthContext";

const FormSlapans = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const formik = useFormik({
    initialValues: {
      name: location.state ? location.state.item.name : "",
      grace_period: location.state ? location.state.item.grace_period : "",
      admin_note: location.state ? location.state.item.admin_note : "",
      status: location.state ? location.state.item.status : 0,
      isdefault: location.state ? location.state.item.isdefault : 0,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      grace_period: Yup.string().required("Grace Periode is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_slaplan,
          formData,
          { dispatch, redux: slaplanReducers },
          "ADD_SLAPLAN",
          () => {
            navigate("/administrator/manage/slaplans");
          }
        );
      } else {
        putData(
          `${API_URL_slaplan}${location.state.item.id}/`,
          formData,
          { dispatch, redux: slaplanReducers },
          "ADD_SLAPLAN",
          () => {
            navigate("/administrator/manage/slaplans");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"SLA plan"}
        excludedPaths={["manage", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          {typeForm === "create" ? "Create" : "Edit"}
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className="h-auto w-auto px-4 pb-6 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-3">
              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-4 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Name <span className="text-red-500 text-xs">*</span>
                </label>
                <div>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.name}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-5 lg:col-span-4 lg:col-start-5">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Grace period
                </label>
                <div>
                  <select
                    id="grace_period"
                    name="grace_period"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[1] = ref)}
                    value={formik.values.grace_period}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select</option>
                    <option value={"6 Hours"}>6 Hours</option>
                    <option value={"12 Hours"}>12 Hours</option>
                    <option value={"18 Hours"}>18 Hours</option>
                    <option value={"24 Hours"}>24 Hours</option>
                    <option value={"36 Hours"}>36 Hours</option>
                    <option value={"48 Hours"}>48 Hours</option>
                  </select>
                  {formik.touched.grace_period &&
                    formik.errors.grace_period && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.grace_period}
                      </div>
                    )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-9 lg:col-span-4 lg:col-start-9">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Status
                </label>
                <div className="flex flex-col flex-wrap">
                  <div>
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        id="status"
                        type="radio"
                        name="status"
                        checked={formik.values.status === 1}
                        onChange={() => formik.setFieldValue("status", 1)}
                        ref={(ref) => (inputRefs.current[2] = ref)}
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2 mr-5">Active</span>
                    </label>
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        id="status"
                        name="status"
                        type="radio"
                        checked={formik.values.status === 0}
                        onChange={() => formik.setFieldValue("status", 0)}
                        ref={(ref) => (inputRefs.current[3] = ref)}
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2">Inactive</span>
                    </label>
                  </div>
                  {formik.touched.status && formik.errors.status && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.status}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1">
              <label className="text-xs mr-1 md:-text-sm lg:text-sm mb-1">
                Admin notes
              </label>
              <div>
                <CKEditor
                  config={toolbarConfig}
                  type="classic"
                  initData={formik.values.admin_note}
                  onChange={(e) => {
                    formik.setFieldValue("admin_note", e.editor.getData());
                  }}
                />
                {formik.touched.admin_note && formik.errors.admin_note && (
                  <div className="text-red-500 dark:text-red-300 text-xs">
                    {formik.errors.admin_note}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default FormSlapans;
